import { useState } from 'react';
import { nanoid } from 'nanoid';
import { useCartAddItems, useSettings } from '@backpackjs/storefront';
import { useSidebar } from '@hooks';
import { byobChargeVariantId } from '@snippets/Cart/LineItems/useByobLineItems';

export const useCustomizer = ({ modal, onClose = () => {}, selectedColor }) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showSavedItems, setShowSavedItems] = useState(false);
  const [shareLink, setShareLink] = useState('');

  const { cartAddItems } = useCartAddItems();
  const [, { openSidebar }] = useSidebar();

  const settings = useSettings();
  const minQuantitySetting = settings?.cart?.minQuantity;
  const buttonsHandler = () => {
    if (!modal) return;

    // Close button
    const closeButton = modal.querySelector('button#close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        modal.classList.add('customizer-hidden');
        document.body.style.overflow = '';
        document.documentElement.classList.add('customizer-open');
        onClose();
      });
    }

    // Share design button
    const shareDesignButton = modal.querySelector('.customizer-share-design');
    const shareButton = modal.querySelector('#customizer-share-button');
    if (shareDesignButton && shareButton) {
      shareDesignButton.addEventListener('click', () => {
        shareButton.click();
      });
    }
  };

  const performReset = () => {
    if (window.newSplide) {
      window.newSplide.go(0);
    }
    if (window.customizer) {
      window.customizer.reset();
    }
    setShowResetConfirm(false);
    document.dispatchEvent(new CustomEvent('vu-reset-tabs'));
  };

  const handleShareEvent = (event) => {
    const shareLinkElement = document.querySelector('#customizer-share-link');
    if (shareLinkElement) {
      setShareLink(shareLinkElement.textContent);
    }
    setShowShareModal(true);
  };

  const handleAddToCartEvent = async (event) => {
    try {
      const currentUrl = new URL(window.location.href);
      const recipeMode = currentUrl.searchParams.get('recipe_mode');
      const quantityInput = modal.querySelector(
        '.customizer-quantity-element-input'
      );
      const quantity = parseInt(quantityInput?.value ?? '1', 10);
      if (recipeMode === 'edit') {
        window.location.href = '/cart';
        return;
      }

      const { token, body: recipeData } = event.detail;

      const parsedRecipeData = Object.keys(recipeData).reduce((acc, key) => {
        if (
          recipeData[key]?.value?.value ||
          recipeData[key]?.value?.code ||
          recipeData[key]?.value?.id
        ) {
          acc[key] =
            recipeData[key]?.value?.value ||
            recipeData[key]?.value?.code ||
            recipeData[key]?.value?.id;
        }

        if (recipeData[key]?.value?.selectedFont?.name) {
          acc[`${key}_font`] = recipeData[key]?.value?.selectedFont.name;
        }
        if (recipeData[key]?.value?.chosenColors?.outline?.name) {
          acc[
            `${key}_color_outline`
          ] = `${recipeData[key]?.value?.chosenColors.outline.name}(${recipeData[key]?.value?.chosenColors.outline.hex})`;
        }
        if (recipeData[key]?.value?.chosenColors?.fill?.name) {
          acc[
            `${key}_color_fill`
          ] = `${recipeData[key]?.value?.chosenColors.fill.name}(${recipeData[key]?.value?.chosenColors.fill.hex})`;
        }
        return acc;
      }, {});

      const relationshipKey = nanoid();
      const thumbnail = `//os.vucustom.com/api/recipes/${token}/images/thumbnail`;
      const personalizationImage = `//os.vucustom.com/api/recipes/${token}/images/Personalization`;
      const props = [
        { key: '_RecipeId', value: token },
        { key: '_AssociationKey', value: relationshipKey },
        { key: '_thumbnail', value: thumbnail },
        { key: '_personalizationImage', value: personalizationImage },
        ...Object.entries(parsedRecipeData).map(([key, value]) => ({
          key: `_customizer_${key}`,
          value: value ? value.toString() : '',
        })),
        {
          key: '_LPROP',
          value: 'rtly-non-returnable',
        },
      ];

      let minQty = quantity;
      if (
        minQuantitySetting?.minimumQuantity != null &&
        minQuantitySetting?.cartItemPropertyKey
      ) {
        const hasProperty = (props || []).some(
          (attr) => attr?.key === minQuantitySetting.cartItemPropertyKey
        );
        if (hasProperty) {
          minQty = Math.max(minQty, minQuantitySetting?.minimumQuantity);
        }
      }
      minQty = parseInt(minQty, 10);

      const items = [];
      if (Object.entries(parsedRecipeData)?.length > 0) {
        items.push({
          merchandiseId: byobChargeVariantId,
          quantity: minQty,
          attributes: [
            {
              key: '_RecipeId',
              value: `uc-${token}`,
            },
            {
              key: '_LPROP',
              value: 'rtly-non-returnable',
            },
          ],
        });
      }
      items.push({
        merchandiseId: `gid://shopify/ProductVariant/${window.customizer.variant}`,
        quantity: minQty,
        attributes: props,
      });
      const cartData = await cartAddItems(items);
      if (cartData) {
        openSidebar('cart');
      }
      performReset();
      onClose();
    } catch (error) {
      document.dispatchEvent(
        new CustomEvent('customizer.add-to-cart-fail', { detail: error })
      );
    }
  };

  const hideColorTab = () => {
    if (selectedColor) {
      const colorTab = modal.querySelector(`#customizer-tab-product_color`);
      const colorTabBody = modal.querySelector(
        `#customizer-tab-body-product_color`
      );
      if (colorTab) {
        colorTab.classList.remove('customizer-tab-selected');
        colorTab.classList.add('!hidden');
        colorTab.classList.remove('customizer-tab');
      }
      if (colorTabBody) {
        colorTabBody.classList.add('hidden');
        colorTabBody.classList.remove('customizer-tab-body');
      }
      const colorValueElement = [
        ...modal.querySelectorAll(
          `#customizer-element-color .customizer-list-element-images-item`
        ),
      ].find((item) =>
        item
          .getAttribute('data-name')
          .toLowerCase()
          .includes(selectedColor.toLowerCase())
      );
      if (colorValueElement) {
        colorValueElement.click();
        colorValueElement.dispatchEvent(new Event('input'));
      }
    }
  };

  // Create Preview tab
  const createPreviewTab = () => {
    const tabContainer = modal.querySelector('#customizer-tab-container');
    if (tabContainer) {
      const previewTab =
        tabContainer.querySelector('#customizer-preview-tab') ||
        document.createElement('div');
      previewTab.className = 'customizer-tab';
      previewTab.id = 'customizer-preview-tab';

      const tabNameDiv = document.createElement('div');
      tabNameDiv.className = 'customizer-tab-name';
      tabNameDiv.textContent = 'Final Design';
      previewTab.innerHTML = '';

      previewTab.appendChild(tabNameDiv);
      tabContainer.appendChild(previewTab);

      // Move the preview tab body to the end of the container
      const tabBodyContainer = modal.querySelector(
        '#customizer-tab-body-container'
      );
      const previewTabBody = modal.querySelector(
        '#customizer-tab-body-preview'
      );
      if (tabBodyContainer && previewTabBody) {
        previewTabBody.className = 'customizer-tab-body';
        previewTabBody.id = 'customizer-tab-body-preview';
        tabBodyContainer.appendChild(previewTabBody);
        previewTabBody.classList.add('vu-main-hidden');
      }
    }
  };

  const setupProgressBar = () => {
    // Initialize progress bar
    const progressBar = modal.querySelector('#customizer-progress-bar');
    const progressContainer = modal.querySelector('.progress-bar-container');

    if (progressBar && progressContainer) {
      // Wait for tabs to be fully loaded before showing progress bar
      setTimeout(() => {
        const selectedTab = modal.querySelector('.customizer-tab-selected');
        if (selectedTab) {
          const tabs = Array.from(modal.querySelectorAll('.customizer-tab'));
          const idx = tabs.indexOf(selectedTab);
          if (idx !== -1) {
            // Set width first while still hidden
            progressBar.style.transition = 'none';
            const progress = ((idx + 1) / tabs.length) * 100;
            progressBar.style.width = `${progress}%`;

            // Show progress bar after width is set and tab counter is visible
            setTimeout(() => {
              progressBar.style.transition =
                'width 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease';
              progressContainer.classList.remove('progress-hidden');
            }, 300);
          }
        }
      }, 600); // Increased delay to ensure tabs and counter are fully loaded
    }
  };

  // Manages saved customizations display and interactions
  const setupSavedItems = () => {
    // Get the elements by their class names
    const customizerSavedItemCount = document.getElementById(
      'customizer-saved-item-count'
    );

    if (customizerSavedItemCount) {
      customizerSavedItemCount.addEventListener('click', () => {
        if (savedItemsRef?.current) {
          window.setTimeout(() => {
            setShowSavedItems(!showSavedItems);
          }, 300);
        }
      });
    }
  };

  // Setup reset button functionality
  const setupResetButton = () => {
    const resetButtons = [
      ...modal.querySelectorAll(
        '.customizer-reset-button, .customizer-clear-button'
      ),
    ];
    if (resetButtons) {
      resetButtons.forEach((resetButton) => {
        resetButton.addEventListener('click', () => {
          if (window.customizer && window.customizer.recipeChanged) {
            setShowResetConfirm(true);
          } else {
            if (window.newSplide) {
              window.newSplide.go(0);
            }
            if (window.customizer) {
              window.customizer.reset();
              document.dispatchEvent(
                new CustomEvent('vu-reset-tabs', {
                  detail: { animateProgressBar: false },
                })
              );
            }
          }
        });
      });
    }
  };

  // Sets up the mobile resize functionality
  const setupMobileResize = () => {
    let isResizing = false;
    let lastDownY = 0;

    const topDiv = modal.querySelector('#customizer-main-perspective');
    const bottomDiv = modal.querySelector('.wrapper-div');
    const handle = modal.querySelector('.bottom-resizer');

    if (!topDiv || !bottomDiv || !handle) {
      return;
    }

    function startResize(e) {
      isResizing = true;
      lastDownY = e.clientY || e.touches[0].clientY;
      e.preventDefault();

      // Disable transitions for smooth resizing
      topDiv.style.transition = 'none';
      bottomDiv.style.transition = 'none';
    }

    function resize(e) {
      if (!isResizing) return;

      const clientY = e.clientY || e.touches[0].clientY;
      const deltaY = clientY - lastDownY;
      const newHeightTop = topDiv.offsetHeight + deltaY;

      if (newHeightTop > 500 || newHeightTop < 210) {
        return;
      }

      const newHeightBottom = bottomDiv.offsetHeight - deltaY;
      topDiv.style.height = `${newHeightTop}px`;
      bottomDiv.style.height = `${newHeightBottom}px`;

      lastDownY = clientY;
      e.preventDefault();
    }

    function stopResize() {
      if (!isResizing) return;
      isResizing = false;

      // Re-enable transitions after resizing
      topDiv.style.transition = '';
      bottomDiv.style.transition = '';
    }

    handle.addEventListener('mousedown', startResize);
    handle.addEventListener('touchstart', startResize, { passive: false });

    document.addEventListener('mousemove', resize);
    document.addEventListener('touchmove', resize, { passive: false });

    document.addEventListener('mouseup', stopResize);
    document.addEventListener('touchend', stopResize);
  };

  const resetView = () => {
    if (window.customizer) {
      window.customizer.resetPerspective(); // not working at the moment
    }
  };

  const setupResetView = () => {
    if (modal) {
      modal.addEventListener('click', (event) => {
        if (event.target.closest('#customizer-reset-view')) {
          resetView();
        }
      });
    }
  };

  const setupSpinnerDrag = () => {
    const state = { dragging: false, startX: 0, item: null };

    function handleEvent(e) {
      const item = e.target.closest
        ? e.target.closest('.list-element-spinner-item')
        : null;
      const clientX = e.type.includes('touch')
        ? (e.touches?.[0] || e.changedTouches?.[0])?.clientX
        : e.clientX;

      if (
        e.type.includes('down') &&
        item?.classList.contains('customizer-selected')
      ) {
        Object.assign(state, { dragging: true, startX: clientX, item });
        item.style.cssText += 'transition:none!important;cursor:grabbing';
        e.preventDefault();
      } else if (e.type.includes('move') && state.dragging) {
        const offset = Math.max(
          -25,
          Math.min(25, (clientX - state.startX) * 0.5)
        );
        state.item.style.setProperty('--drag-offset', `${offset}px`);
        e.preventDefault();
      } else if (
        (e.type.includes('up') || e.type === 'mouseleave') &&
        state.dragging
      ) {
        const deltaX = (clientX || state.startX) - state.startX;
        state.item.style.removeProperty('transition');
        state.item.style.cursor = '';
        state.item.style.removeProperty('--drag-offset');

        if (Math.abs(deltaX) > 20) {
          const container = state.item.closest(
            '.list-element-spinner-container'
          );
          const spinner = container?.closest('.list-element-spinner');
          const items = container.querySelectorAll(
            '.list-element-spinner-item'
          );
          const currentIdx = [...items].findIndex((i) =>
            i.classList.contains('customizer-selected')
          );
          const newIdx = deltaX > 0 ? currentIdx + 1 : currentIdx - 1;

          if (newIdx >= 0 && newIdx < items.length) {
            const btn = spinner.querySelector(
              deltaX > 0
                ? '#list-element-spinner-right, .list-element-spinner-button-right'
                : '#list-element-spinner-left, .list-element-spinner-button-left'
            );
            btn?.click();
          }
        }
        Object.assign(state, { dragging: false, item: null });
      }
    }

    [
      'mousedown',
      'touchstart',
      'mousemove',
      'touchmove',
      'mouseup',
      'touchend',
      'mouseleave',
    ].forEach((type) =>
      document.addEventListener(
        type,
        handleEvent,
        type.includes('touch') ? { passive: false } : false
      )
    );
  };

  const updateButtonState = () => {
    const nextBtn = modal.querySelector('#customizer-tab-next');
    const clearBtn = modal.querySelector('.customizer-clear-button');
    const currentTab = window.customizer.tabs.current();
    if (currentTab && currentTab.valid) {
      nextBtn.disabled = false;
      nextBtn.removeAttribute('disabled');
      nextBtn.classList.remove('disabled');
    } else {
      nextBtn.disabled = true;
      nextBtn.setAttribute('disabled', 'disabled');
      nextBtn.classList.add('disabled');
    }
    if (clearBtn) {
      if (!window.customizer.recipeValuesChanged) {
        clearBtn.disabled = true;
        clearBtn.setAttribute('disabled', 'disabled');
        clearBtn.classList.add('disabled');
      } else {
        clearBtn.disabled = false;
        clearBtn.removeAttribute('disabled');
        clearBtn.classList.remove('disabled');
      }
    }
  };

  const setupTabNavigation = () => {
    hideColorTab();
    const tabContainer = modal.querySelector('#customizer-tab-container');
    const tabs = Array.from(
      tabContainer.querySelectorAll(
        '.customizer-tab:not(#customizer-tab-product_color)'
      )
    );
    const prevBtn = modal.querySelector('#customizer-tab-prev');
    const nextBtn = modal.querySelector('#customizer-tab-next');
    const clearBtn = modal.querySelector(
      '.wrapper-bottom-nav .customizer-clear-button'
    );
    const backBtn = modal.querySelector('.customizer-tab-body-back-button');
    const counter = modal.querySelector('#customizer-tab-counter');
    const currentNameContainer = modal.querySelector(
      '#customizer-tab-current-name'
    );
    const atcButton = modal.querySelector(
      '.wrapper-bottom-nav .customizer-add-to-cart-button'
    );
    const addToCartButton = modal.querySelector(
      '.wrapper-bottom-nav .customizer-add-to-cart'
    );
    const atcContainer = modal.querySelector('.customizer-atc-container');
    const progressBar = modal.querySelector('#customizer-progress-bar');

    // Initialize progress bar to 0% to prevent flashing to 100%
    if (progressBar) {
      progressBar.style.width = '0%';
    }

    // Add numbered classes to tabs
    tabs.forEach((tab, index) => {
      tab.classList.add(`customizer-tab-${index + 1}`);
    });

    function setupTabConfig() {
      const tabConfig = Object.keys(window.customizer?.tabs || {});
      tabConfig.forEach((tab) => {
        const parentElement = modal.querySelector(`#customizer-element-${tab}`);
        const summaryItem = modal.querySelector(
          `.customizer-summary-item-${tab}`
        );
        if (summaryItem) {
          summaryItem.classList.add(`customizer-summary-item-${tab}`);
        }
        if (parentElement) {
          parentElement.classList.add(`customizer-element-parent`);
          const childElements = parentElement
            .closest('.customizer-tab-body-elements')
            .querySelectorAll(
              ':scope > .customizer-element:not(.customizer-element-parent)'
            );
          childElements.forEach((element) => {
            element.classList.add('customizer-hidden');
          });
          parentElement.classList.remove('customizer-hidden');
          backBtn.classList.add('!hidden');
        }
      });
    }

    // Returns the index of the currently selected tab
    function getSelectedIndex() {
      return tabs.findIndex((tab) =>
        tab.classList.contains('customizer-tab-selected')
      );
    }

    // Updates the step counter display with animation
    function updateCounter() {
      const idx = getSelectedIndex();
      // Trigger CSS animation by temporarily removing and re-adding
      counter.style.animation = 'none';
      counter.offsetHeight; // Trigger reflow
      counter.style.animation = 'fadeInOut 0.6s ease-in-out';
      counter.textContent = `${idx + 1} / ${tabs.length}`;
    }

    // Updates the current tab name display in navigation
    function updateCurrentTabName(idx) {
      if (idx < 0 || idx >= tabs.length) return;
      // Move the .customizer-tab-name node to the nav
      const tabName = tabs[idx].querySelector('.customizer-tab-name');
      const tabItemName = tabs[idx].querySelector(
        '.customizer-tab-summary-item-element-name'
      );
      const tabTitle = modal.querySelector('.customizer-tab-body-title');
      if (tabName && currentNameContainer) {
        if (tabTitle) {
          tabTitle.textContent = tabItemName
            ? tabItemName.textContent.trim()
            : tabName.textContent.trim();
          if (idx < tabs.length - 1) {
            tabTitle.classList.add('hidden');
          } else {
            tabTitle.classList.remove('hidden');
          }
        }
        // Trigger CSS animation by temporarily removing and re-adding
        currentNameContainer.style.animation = 'none';
        currentNameContainer.offsetHeight; // Trigger reflow
        currentNameContainer.style.animation = 'fadeInOut 0.6s ease-in-out';

        currentNameContainer.innerHTML = '';
        const newTabName = tabName.cloneNode(true);
        currentNameContainer.appendChild(newTabName);

        const clonedShareButton = newTabName.querySelector(
          '#customizer-share-button'
        );
        if (clonedShareButton) {
          clonedShareButton.id = 'customizer-share-button-cloned'; // Avoid duplicate IDs
          clonedShareButton.addEventListener('click', () => {
            const originalShareButton = document.getElementById(
              'customizer-share-button'
            );
            if (originalShareButton) originalShareButton.click();
          });
        }
      }
    }

    // Shows/hides navigation buttons based on current tab position
    function updateNavigationButtons(idx) {
      if (!modal) return;
      const shareDesignButton = modal.querySelector('.customizer-share-design');

      const isLastTab = idx === tabs.length - 1;
      const isFirstTab = idx === 0;
      prevBtn.classList.add('hidden');
      if (isFirstTab && !window.customizer.recipeValuesChanged) {
        // clearBtn.classList.add('hidden');
        clearBtn.disabled = true;
        clearBtn.setAttribute('aria-disabled', true);
        clearBtn.setAttribute('disabled', true);
      } else {
        // clearBtn.classList.remove('hidden');
        clearBtn.disabled = false;
        clearBtn.setAttribute('aria-disabled', false);
        clearBtn.removeAttribute('disabled');
      }
      if (isLastTab) {
        // Hide next button and show add to cart button on last tab
        nextBtn.classList.add('hidden');
        // prevBtn.classList.remove('hidden');
        nextBtn.classList.add('hidden');
        nextBtn.disabled = true;
        nextBtn.setAttribute('aria-disabled', true);
        nextBtn.setAttribute('disabled', true);
        prevBtn.disabled = false;
        prevBtn.setAttribute('aria-disabled', false);
        prevBtn.removeAttribute('disabled');
        if (atcContainer) {
          atcContainer.classList.remove('hidden');
          atcContainer.classList.add('flex');
        }
        if (shareDesignButton) {
          shareDesignButton.classList.remove('hidden');
          shareDesignButton.classList.add('flex');
        }
        if (atcButton) {
          // atcButton.style.display = 'block';
        }
      } else {
        // Show next button and hide add to cart button on other tabs
        nextBtn.classList.remove('hidden');
        nextBtn.disabled = false;
        nextBtn.setAttribute('aria-disabled', false);
        nextBtn.removeAttribute('disabled');
        if (atcContainer) {
          atcContainer.classList.add('hidden');
          atcContainer.classList.remove('flex');
        }
        if (atcButton) {
          // atcButton.style.display = 'none';
        }
        if (isFirstTab) {
          // prevBtn.classList.add('hidden');
          if (shareDesignButton) {
            shareDesignButton.classList.add('hidden');
            shareDesignButton.classList.remove('flex');
          }
          prevBtn.disabled = true;
          prevBtn.setAttribute('aria-disabled', true);
          prevBtn.setAttribute('disabled', true);
        }
      }
    }

    // Updates the progress bar based on current tab position
    function updateProgressBar(idx) {
      const progressBar = modal.querySelector('#customizer-progress-bar');
      if (progressBar) {
        // Ensure opacity is set to 1
        progressBar.style.opacity = '1';

        // Reset to 0 first to ensure animation works correctly
        progressBar.style.transition = 'none';
        progressBar.offsetHeight; // Force reflow

        // Calculate progress based on current tab index
        const progress = ((idx + 1) / tabs.length) * 100;

        // Re-enable transition and set the width
        progressBar.style.transition =
          'width 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease';
        progressBar.style.width = `${progress}%`;
      }
    }

    // Selects a tab and updates all UI elements accordingly
    function selectTab(idx, options = {}) {
      const colorTab = modal.querySelector(`#customizer-tab-product_color`);
      if (colorTab) {
        colorTab.classList.remove('customizer-tab-selected');
      }
      tabs.forEach((tab) => tab.classList.remove('customizer-tab-selected'));
      tabs[idx].classList.add('customizer-tab-selected');
      updateCounter();
      updateCurrentTabName(idx);
      updateNavigationButtons(idx);
      if (!options.isReset) {
        updateProgressBar(idx);
      }

      const isPreviewTab = tabs[idx].id === 'customizer-preview-tab';
      const tabBodyContainer = document.getElementById(
        'customizer-tab-body-container'
      );
      const allOriginalTabBodies = Array.from(
        tabBodyContainer.querySelectorAll(
          '.customizer-tab-body:not(#customizer-tab-body-preview):not(#customizer-tab-body-product_color)'
        )
      );
      const previewBody = document.getElementById(
        'customizer-tab-body-preview'
      );

      if (isPreviewTab) {
        // When switching to the preview tab, hide all original tab bodies
        // and show the preview body.
        allOriginalTabBodies.forEach((body) => {
          body.classList.add('vu-main-hidden');
          body.classList.remove('customizer-body-selected');
        });
        if (previewBody) {
          previewBody.classList.remove('vu-main-hidden', 'customizer-hidden');
          previewBody.classList.add('customizer-body-selected');
        }
      } else {
        // When switching back to an original tab, hide the preview body.
        if (previewBody) {
          previewBody.classList.add('vu-main-hidden');
          previewBody.classList.remove('customizer-body-selected');
        }

        // Remove our custom hidden class from all original bodies so the
        // original script can take over.
        allOriginalTabBodies.forEach((body) =>
          body.classList.remove(
            'vu-main-hidden',
            'customizer-hidden',
            'customizer-body-selected'
          )
        );
        allOriginalTabBodies[idx].classList.add('customizer-body-selected');
        const tabName = tabs[idx].querySelector('.customizer-tab-name');
        if (tabName) tabName.click();
      }
    }

    prevBtn.onclick = () => {
      const idx = getSelectedIndex();
      if (idx > 0) selectTab(idx - 1);
    };

    nextBtn.onclick = () => {
      const idx = getSelectedIndex();
      if (idx < tabs.length - 1) selectTab(idx + 1);
    };

    backBtn.onclick = () => {
      const selectedTabBody = modal.querySelector('.customizer-body-selected');
      if (
        selectedTabBody &&
        selectedTabBody?.id === 'customizer-tab-body-preview'
      ) {
        const idx = getSelectedIndex();
        if (idx > 0) selectTab(idx - 1);
        return;
      }
      if (selectedTabBody) {
        // backBtn.classList.add('customizer-hidden');
        const parentElement = selectedTabBody.querySelector(
          '.customizer-tab-body-elements .customizer-element-parent'
        );
        const subElements = Array.from(
          selectedTabBody.querySelectorAll(
            '.customizer-tab-body-elements > .customizer-element'
          )
        );
        subElements.forEach((element) => {
          element.classList.add('customizer-hidden');
        });
        if (parentElement) {
          parentElement.classList.remove('customizer-hidden');
          const inputElements = Array.from(
            parentElement.querySelectorAll('input')
          );
          inputElements.forEach((input) => {
            input.checked = false;
          });
          backBtn.classList.add('!hidden');
        }
      }
    };

    // Add to cart logic
    if (addToCartButton && atcButton) {
      addToCartButton.addEventListener('click', () => {
        atcButton.click();
      });
    }

    // Make tabs clickable
    tabs.forEach((tab, idx) => {
      tab.addEventListener('click', () => {
        if (!tab.classList.contains('customizer-tab-selected')) {
          selectTab(idx);
        }
      });
    });

    // Listen for reset event to select the first tab
    document.addEventListener('vu-reset-tabs', (e) => {
      hideColorTab();
      const animateProgressBar = e.detail?.animateProgressBar ?? true;
      // isReset being true will *prevent* the progress bar from animating in selectTab
      window.setTimeout(() => {
        selectTab(0, { isReset: !animateProgressBar });
        setupTabConfig();
      }, 600);
    });

    // Initial counter, tab name, and navigation buttons
    let initialIndex = getSelectedIndex();
    if (initialIndex === -1 && tabs.length > 0) {
      initialIndex = 0;
      tabs[initialIndex].classList.add('customizer-tab-selected');
    }

    if (initialIndex !== -1) {
      selectTab(initialIndex);
      setupTabConfig();
      updateProgressBar(initialIndex);
    }

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (modal && !modal.classList.contains('customizer-hidden')) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          prevBtn.click();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          nextBtn.click();
        }
      }
    });
  };

  const handleQuantityAction = () => {
    const maxQty = 10;
    const qtyInCart = 0;
    const quantityDecrementButton = modal.querySelector(
      '.customizer-quantity-decrement-button'
    );
    const quantityIncrementButton = modal.querySelector(
      '.customizer-quantity-increment-button'
    );
    const quantityInput = modal.querySelector('.customizer-quantity-input');
    if (quantityDecrementButton) {
      quantityDecrementButton.addEventListener('click', () => {
        const currentQuantity = parseInt(quantityInput?.value ?? '1', 10);
        const newQuantity = Math.max(1, currentQuantity - 1);
        quantityInput.value = newQuantity;
        if (newQuantity <= 1) {
          quantityDecrementButton.disabled = true;
        }
        if (newQuantity < maxQty - qtyInCart) {
          quantityIncrementButton.disabled = false;
        } else {
          quantityIncrementButton.disabled = true;
        }
      });
    }
    if (quantityIncrementButton) {
      quantityIncrementButton.addEventListener('click', () => {
        const currentQuantity = parseInt(quantityInput?.value ?? '1', 10);
        const newQuantity = Math.min(maxQty - qtyInCart, currentQuantity + 1);
        quantityInput.value = newQuantity;
        if (newQuantity >= maxQty - qtyInCart) {
          quantityIncrementButton.disabled = true;
        }
        if (quantityDecrementButton && newQuantity > 1) {
          quantityDecrementButton.disabled = false;
        }
      });
    }
    if (quantityInput) {
      quantityInput.addEventListener('change', (e) => {
        const newQuantity = parseInt(e.target.value, 10) || 1;
        if (newQuantity <= 1) {
          quantityDecrementButton.disabled = true;
        } else if (newQuantity >= maxQty - qtyInCart) {
          quantityIncrementButton.disabled = true;
        } else {
          quantityDecrementButton.disabled = false;
          quantityIncrementButton.disabled = false;
        }
      });
    }
  };

  const handleRecipeChange = () => {
    if (!modal) return;

    setupProgressBar();
    const summarySpecs = modal.querySelector('#customizer-summary-specs');
    const backBtn = modal.querySelector('.customizer-tab-body-back-button');
    const parentElement = modal.querySelector('.customizer-element-parent');
    const customizerTabBodyElements = parentElement.closest(
      '.customizer-tab-body-elements'
    );
    const customizerVisibleElement = customizerTabBodyElements.querySelector(
      ':scope > .customizer-element:not(.customizer-element-parent):not(.customizer-hidden)'
    );
    if (customizerVisibleElement) {
      if (backBtn) {
        backBtn.classList.remove('!hidden');
      }
      parentElement.classList.add('customizer-hidden');
    } else {
      parentElement.classList.remove('customizer-hidden');
    }

    if (summarySpecs) {
      summarySpecs.innerHTML = '';
      const summarySpecsItems = modal.querySelector(
        '#customizer-summary-items'
      );
      if (summarySpecsItems) {
        summarySpecs.innerHTML = summarySpecsItems.innerHTML;
      }
    }

    const sidebarHeader = modal.querySelector('.customizer-header');
    if (sidebarHeader) {
      const headerHeight = sidebarHeader.getBoundingClientRect().height;
      document.documentElement.style.setProperty(
        '--customizer-header-height',
        `${headerHeight}px`
      );
    }
  };

  const wrap = (el, wrapper) => {
    if (el && el.parentNode) {
      el.parentNode.insertBefore(wrapper, el);
      wrapper.appendChild(el);
    }
  };

  const elementsCustomizer = () => {
    const elements = Array.from(
      modal.querySelectorAll('.customizer-list-element-images-item')
    );
    elements.forEach((element) => {
      const name = element.getAttribute('data-tags');
      const elWrapper = document.createElement('div');
      elWrapper.className = 'customizer-list-element-images-item-wrapper';
      const elementName = document.createElement('div');
      elementName.className = 'customizer-list-element-images-item-name hidden';
      elementName.textContent = name;
      if (!element.closest('.customizer-list-element-images-item-wrapper')) {
        wrap(element, elWrapper);
        elWrapper.appendChild(elementName);
      }
    });
    const quantityInputElement = modal.querySelector(
      '#customizer-element-quantity'
    );
    const quantityContainer = modal.querySelector(
      '#customizer-element-quantity-container'
    );
    if (quantityInputElement && quantityContainer) {
      quantityContainer.insertAdjacentElement(
        'afterbegin',
        quantityInputElement
      );
    }
  };

  const setupElementsListeners = () => {
    const filterDropdown = modal.querySelector(
      'select.customizer-tags-filter-dropdown'
    );
    if (filterDropdown) {
      const wrapper = filterDropdown.closest('.customizer-list-element');
      const imgElements = Array.from(
        wrapper.querySelectorAll(
          '.customizer-element-value-container .customizer-list-element-images-item'
        )
      );
      filterDropdown.addEventListener('change', (e) => {
        imgElements.forEach((img) => {
          const imgWrapper = img.closest(
            '.customizer-list-element-images-item-wrapper'
          );
          if (imgWrapper) {
            if (img.classList.contains('customizer-hidden')) {
              imgWrapper.classList.add('customizer-hidden');
            } else {
              imgWrapper.classList.remove('customizer-hidden');
            }
          }
        });
      });
    }

    const radioElements = Array.from(
      modal.querySelectorAll(
        '.customizer-element-parent .customizer-list-element-radio-item input'
      )
    );
    const sidebarContent = modal.querySelector('.customizer-sidebar-content');
    radioElements.forEach((radio) => {
      radio.checked = false;
      radio.addEventListener('change', (e) => {
        if (sidebarContent) {
          sidebarContent.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      });
    });
  };

  const customizerInit = () => {
    if (!modal) return;
    elementsCustomizer();
    buttonsHandler();
    setupProgressBar();
    setupSavedItems();
    setupResetButton();
    setupTabNavigation();
    setupElementsListeners();
    setupMobileResize();
    setupResetView();
    setupSpinnerDrag();
    handleQuantityAction();
  };

  return {
    state: {
      showShareModal,
      showResetConfirm,
      showSavedItems,
      shareLink,
    },
    actions: {
      performReset,
      setShowShareModal,
      setShowResetConfirm,
      setShowSavedItems,
      handleShareEvent,
      handleAddToCartEvent,
      customizerInit,
      handleRecipeChange,
      updateButtonState,
    },
  };
};
